import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  TextInput,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  getIssueReports,
  updateIssueReportStatus,
  getOpportunityById,
} from "../../services/parseService";
import { IssueReport } from "../../types";
import { format, parseISO } from "date-fns";

interface IssueReportsScreenProps {
  onReloadIssueCount: () => void;
}

type IssueStatus = "Pending" | "Reviewed" | "Resolved" | "Rejected";

const IssueReportsScreen: React.FC<IssueReportsScreenProps> = ({
  onReloadIssueCount,
}) => {
  const [issues, setIssues] = useState<any[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<IssueStatus | "All">(
    "Pending",
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Modal state
  const [selectedIssue, setSelectedIssue] = useState<any | null>(null);
  const [isIssueModalVisible, setIsIssueModalVisible] = useState(false);
  const [issueNotes, setIssueNotes] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [opportunityDetails, setOpportunityDetails] = useState<any | null>(
    null,
  );
  const [isLoadingOpportunity, setIsLoadingOpportunity] = useState(false);
 
  // Track whether we've already called onReloadIssueCount
  const parentNotifiedRef = React.useRef(false);
  // Track the last time we notified the parent
  const lastNotifyTimeRef = React.useRef(0);

  // Simplify the notification mechanism
  const notifyParentOnce = () => {
    const now = Date.now();
    // Only notify if we haven't before or if it's been more than 30 seconds
    if (!parentNotifiedRef.current || now - lastNotifyTimeRef.current > 30000) {
      console.log("🔍 Notifying parent about issue count");
      onReloadIssueCount();
      parentNotifiedRef.current = true;
      lastNotifyTimeRef.current = now;
    } else {
      console.log(
        "🔍 Skipping parent notification - already notified or too frequent",
      );
    }
  };

  useEffect(() => {
    console.log(
      "🔍 IssueReportsScreen mounted or statusFilter changed: ",
      statusFilter,
    );

    let isMounted = true;

    // Reset loading state when the filter changes
    setIsLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        console.log(
          "🔍 Fetching issue reports with status filter:",
          statusFilter !== "All" ? statusFilter : "All (undefined)",
        );

        // Get status filter value for the API call
        const status = statusFilter !== "All" ? statusFilter : undefined;

        // Fetch issues from the API
        const results = await getIssueReports(status);
        console.log(
          `🔍 Received ${results?.length || 0} issue reports from API`,
        );

        // Only update state if component is still mounted
        if (!isMounted) {
          console.log("🔍 Component unmounted during fetch, abandoning update");
          return;
        }

        if (!Array.isArray(results)) {
          console.error("🔍 API returned non-array results:", results);
          throw new Error("Invalid response format from server");
        }

        // Process the results
        const processedIssues = processIssueResults(results);
        console.log(
          `🔍 Processed ${processedIssues.length} valid issue objects`,
        );

        // Update state
        setIssues(processedIssues);
        setFilteredIssues(processedIssues);

        // Notify parent once if we have pending issues
        if (processedIssues.length > 0 && statusFilter === "Pending") {
          notifyParentOnce();
        }
      } catch (error) {
        console.error("🔍 Error fetching issue reports:", error);

        if (isMounted) {
          setError("Failed to load issues. Please try again.");
          setIssues([]);
          setFilteredIssues([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Execute fetch immediately
    fetchData();

    // Cleanup function
    return () => {
      console.log("🔍 IssueReportsScreen cleanup - component unmounting");
      isMounted = false;
      parentNotifiedRef.current = false;
    };
  }, [statusFilter]); // Only re-run when statusFilter changes

  useEffect(() => {
    if (issues.length > 0) {
      filterIssues();
    }
  }, [issues, statusFilter, searchQuery]);

  // Process issue results from Parse into usable objects with robust error handling
  const processIssueResults = (results: any[]) => {
    if (!Array.isArray(results)) {
      console.error("❌ processIssueResults received non-array data:", results);
      return [];
    }

    // Convert Parse objects to regular objects
    return results
      .map((issue: any, index: number) => {
        try {
          // Handle different possible formats of Parse objects
          const id = issue.id || issue.objectId;

          // Handle cases where data could be in different structures
          let attrs: any = {};

          if (issue.attributes) {
            // Standard Parse object format
            attrs = issue.attributes;
          } else if (issue.get && typeof issue.get === "function") {
            // Parse object with get function but no direct attributes access
            try {
              // Try to extract all common fields
              attrs = {
                issueType: issue.get("issueType"),
                description: issue.get("description"),
                status: issue.get("status"),
                contactEmail: issue.get("contactEmail"),
                adminNotes: issue.get("adminNotes"),
                createdAt: issue.get("createdAt"),
                updatedAt: issue.get("updatedAt"),
                opportunity: issue.get("opportunity"),
              };
            } catch (getError) {
              console.error(
                `❌ Error getting attributes with get() for issue ${index}:`,
                getError,
              );
            }
          } else {
            // Raw object (e.g., from REST API)
            attrs = issue;
          }

          // Process dates with robust error handling
          const parseDate = (dateObject: any) => {
            try {
              if (!dateObject) return null;

              // Handle Parse server date format
              if (
                dateObject &&
                dateObject.__type === "Date" &&
                dateObject.iso
              ) {
                return dateObject.iso;
              }

              // Handle direct iso property
              if (dateObject && dateObject.iso) {
                return dateObject.iso;
              }

              // Handle Date objects
              if (dateObject instanceof Date) {
                return dateObject.toISOString();
              }

              // Handle ISO string directly
              if (typeof dateObject === "string") {
                // Verify that it's a valid date string
                const testDate = new Date(dateObject);
                if (!isNaN(testDate.getTime())) {
                  return dateObject;
                }
              }

              // If we can't process it, log and return null
              console.warn(`❌ Unprocessable date format:`, dateObject);
              return null;
            } catch (dateError) {
              console.error("❌ Error parsing date:", dateError, dateObject);
              return null;
            }
          };

          // Process opportunity data with better error handling
          let opportunity = null;
          try {
            if (attrs.opportunity) {
              // Handle different opportunity formats
              if (attrs.opportunity.id || attrs.opportunity.objectId) {
                // Direct object with id
                opportunity = {
                  id: attrs.opportunity.id || attrs.opportunity.objectId,
                  title:
                    attrs.opportunity.attributes?.title ||
                    attrs.opportunity.title ||
                    "Unknown Title",
                  organization:
                    attrs.opportunity.attributes?.organization ||
                    attrs.opportunity.organization ||
                    "",
                  type:
                    attrs.opportunity.attributes?.type ||
                    attrs.opportunity.type ||
                    "",
                  status:
                    attrs.opportunity.attributes?.status ||
                    attrs.opportunity.status ||
                    "",
                };
              } else if (
                attrs.opportunity.get &&
                typeof attrs.opportunity.get === "function"
              ) {
                // Parse object with get method
                opportunity = {
                  id: attrs.opportunity.id,
                  title: attrs.opportunity.get("title") || "Unknown Title",
                  organization: attrs.opportunity.get("organization") || "",
                  type: attrs.opportunity.get("type") || "",
                  status: attrs.opportunity.get("status") || "",
                };
              }
            }
          } catch (oppError) {
            console.error(
              `❌ Error processing opportunity data for issue ${id}:`,
              oppError,
            );
            opportunity = { id: "error", title: "Error Loading Opportunity" };
          }

          return {
            id: id || `temp-${index}`,
            issueType: attrs.issueType || "Other",
            description: attrs.description || "No description provided",
            status: attrs.status || "Pending",
            contactEmail: attrs.contactEmail || null,
            adminNotes: attrs.adminNotes || "",
            createdAt: attrs.createdAt ? parseDate(attrs.createdAt) : null,
            updatedAt: attrs.updatedAt ? parseDate(attrs.updatedAt) : null,
            opportunity,
          };
        } catch (processError) {
          console.error(
            `❌ Error processing issue at index ${index}:`,
            processError,
            issue,
          );
          // Return a placeholder object for issues that can't be properly processed
          return {
            id: `error-${index}`,
            issueType: "Error",
            description: "There was an error processing this issue data",
            status: "Pending",
            contactEmail: null,
            adminNotes: "",
            createdAt: null,
            updatedAt: null,
            opportunity: null,
          };
        }
      })
      .filter((issue) => !!issue.id); // Filter out any issues without a valid ID
  };

  // Load issues function for manual retries
  const loadIssues = async () => {
    console.log("🔍 Manual retry requested for loading issues");
    try {
      setIsLoading(true);
      setError(null);

      console.log(
        "🔍 Fetching issues with filter:",
        statusFilter !== "All" ? statusFilter : "All (undefined)",
      );
      const status = statusFilter !== "All" ? statusFilter : undefined;

      const results = await getIssueReports(status);
      console.log(
        `🔍 Manual retry received ${results?.length || 0} issue reports`,
      );

      if (!Array.isArray(results)) {
        console.error(
          "🔍 API returned non-array results on manual retry:",
          results,
        );
        throw new Error("Invalid response format from server");
      }

      const parsedIssues = processIssueResults(results);
      console.log(
        `🔍 Manual retry processed ${parsedIssues.length} valid issue objects`,
      );

      setIssues(parsedIssues);
      setFilteredIssues(parsedIssues);

      // Notify parent if this is first successful load
      if (!parentNotifiedRef.current && parsedIssues.length > 0) {
        console.log("🔍 Notifying parent after manual retry");
        notifyParentOnce();
        parentNotifiedRef.current = true;
      }
    } catch (err) {
      console.error("🔍 Error during manual retry:", err);
      setError("Failed to load issues. Please try again.");
      setIssues([]);
      setFilteredIssues([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterIssues = () => {
    let filtered = [...issues];

    // Apply status filter if not already filtered by API
    if (statusFilter !== "All") {
      filtered = filtered.filter((issue) => issue.status === statusFilter);
    }

    // Apply search query
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (issue) =>
          (issue.description &&
            issue.description.toLowerCase().includes(query)) ||
          (issue.opportunity &&
            issue.opportunity.title &&
            issue.opportunity.title.toLowerCase().includes(query)) ||
          (issue.contactEmail &&
            issue.contactEmail.toLowerCase().includes(query)),
      );
    }

    // Only update state if the filtered results have changed
    if (JSON.stringify(filtered) !== JSON.stringify(filteredIssues)) {
      setFilteredIssues(filtered);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (err) {
      return "Invalid date";
    }
  };

  const handleViewIssue = async (issue: any) => {
    setSelectedIssue(issue);
    setIssueNotes(issue.adminNotes || "");
    setIsIssueModalVisible(true);

    // Load opportunity details if available
    if (issue.opportunity && issue.opportunity.id) {
      await loadOpportunityDetails(issue.opportunity.id);
    }
  };

  const loadOpportunityDetails = async (opportunityId: string) => {
    try {
      setIsLoadingOpportunity(true);
      const opportunity = await getOpportunityById(opportunityId);

      if (opportunity) {
        // Extract and format opportunity data
        const attrs = opportunity.attributes || opportunity;
        setOpportunityDetails({
          id: opportunity.id,
          title: attrs.title,
          organization: attrs.organization,
          type: attrs.type,
          status: attrs.status,
        });
      }
    } catch (err) {
      console.error("Error loading opportunity details:", err);
      // Don't set error, just set opportunityDetails to null
      setOpportunityDetails(null);
    } finally {
      setIsLoadingOpportunity(false);
    }
  };

  const handleUpdateStatus = async (newStatus: IssueStatus) => {
    if (!selectedIssue) return;

    try {
      setIsUpdatingStatus(true);

      await updateIssueReportStatus(selectedIssue.id, newStatus, issueNotes);

      // Update local state
      const updatedIssues = issues.map((issue) =>
        issue.id === selectedIssue.id
          ? { ...issue, status: newStatus, adminNotes: issueNotes }
          : issue,
      );

      setIssues(updatedIssues);

      // Close modal
      setIsIssueModalVisible(false);
      setSelectedIssue(null);

      // Show success message
      Alert.alert("Success", `Issue status updated to ${newStatus}`);

      // Schedule parent component's count update after the current cycle
      setTimeout(() => {
        if (statusFilter === "All" || statusFilter === newStatus) {
          notifyParentOnce();
        }
      }, 100);
    } catch (err) {
      console.error("Error updating issue status:", err);
      Alert.alert("Error", "Failed to update issue status. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: IssueStatus) => {
    switch (status) {
      case "Pending":
        return "#F6AD55";
      case "Reviewed":
        return "#4299E1";
      case "Resolved":
        return "#48BB78";
      case "Rejected":
        return "#F56565";
      default:
        return "#A0AEC0";
    }
  };

  const renderStatusFilter = () => {
    const statuses: (IssueStatus | "All")[] = [
      "All",
      "Pending",
      "Reviewed",
      "Resolved",
      "Rejected",
    ];

    return (
      <View style={styles.statusFilterContainer} data-oid="986l.0u">
        {statuses.map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.statusFilterButton,
              statusFilter === status && styles.statusFilterButtonActive,
              status !== "All" && {
                borderColor: getStatusColor(status as IssueStatus),
                backgroundColor:
                  statusFilter === status
                    ? getStatusColor(status as IssueStatus) + "20" // 20% opacity
                    : "transparent",
              },
            ]}
            onPress={() => setStatusFilter(status)}
            data-oid="m86nris"
          >
            <Text
              style={[
                styles.statusFilterText,
                statusFilter === status && styles.statusFilterTextActive,
                status !== "All" && {
                  color: getStatusColor(status as IssueStatus),
                },
              ]}
              data-oid="qesuux6"
            >
              {status}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const renderItem = ({ item }: { item: any }) => (
    <TouchableOpacity
      style={styles.issueCard}
      onPress={() => handleViewIssue(item)}
      data-oid="w.ckpsp"
    >
      <View style={styles.issueHeader} data-oid="9dti2:b">
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(item.status as IssueStatus) },
          ]}
          data-oid="r-sj37b"
        >
          <Text style={styles.statusText} data-oid="s:u78j4">
            {item.status}
          </Text>
        </View>
        <Text style={styles.dateText} data-oid="yqliv1c">
          {formatDate(item.createdAt)}
        </Text>
      </View>

      <View style={styles.issueContent} data-oid="_7dk:6v">
        <View style={styles.issueTypeContainer} data-oid="s6dr6lj">
          <Ionicons
            name={
              item.issueType === "Broken Link"
                ? "link"
                : item.issueType === "Incorrect Dates"
                  ? "calendar"
                  : item.issueType === "Invalid Information"
                    ? "alert-circle"
                    : "help-circle"
            }
            size={18}
            color="#718096"
            data-oid="0-:94w-"
          />

          <Text style={styles.issueType} data-oid="fkoy86k">
            {item.issueType}
          </Text>
        </View>

        <Text
          style={styles.issueDescription}
          numberOfLines={3}
          data-oid="gnlc1o6"
        >
          {item.description}
        </Text>

        {item.opportunity && (
          <View style={styles.opportunityReference} data-oid="nto3235">
            <Text style={styles.opportunityLabel} data-oid="2wi-7ej">
              Related to:{" "}
            </Text>
            <Text
              style={styles.opportunityTitle}
              numberOfLines={1}
              data-oid="3axaxbg"
            >
              {item.opportunity.title}
            </Text>
          </View>
        )}

        {item.contactEmail && (
          <View style={styles.contactContainer} data-oid="ejozsfs">
            <Ionicons
              name="mail-outline"
              size={14}
              color="#718096"
              data-oid="88w995j"
            />

            <Text style={styles.contactEmail} data-oid="3i70g5i">
              {item.contactEmail}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  const renderIssueModal = () => {
    if (!selectedIssue) return null;

    return (
      <Modal
        visible={isIssueModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsIssueModalVisible(false)}
        data-oid="zeqnnf5"
      >
        <View style={styles.modalOverlay} data-oid="x588rsr">
          <View style={styles.modalContainer} data-oid="xxvnctz">
            <View style={styles.modalHeader} data-oid="v227:h.">
              <Text style={styles.modalTitle} data-oid="j4xgl16">
                Issue Details
              </Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setIsIssueModalVisible(false)}
                data-oid="56vfmwm"
              >
                <Ionicons
                  name="close"
                  size={24}
                  color="#4A5568"
                  data-oid="-v5xtv8"
                />
              </TouchableOpacity>
            </View>

            <View style={styles.modalContent} data-oid="3_g:d8:">
              <View style={styles.modalSection} data-oid="ym:4q9s">
                <View style={styles.modalSectionHeader} data-oid="yf6zk1v">
                  <Ionicons
                    name={
                      selectedIssue.issueType === "Broken Link"
                        ? "link"
                        : selectedIssue.issueType === "Incorrect Dates"
                          ? "calendar"
                          : selectedIssue.issueType === "Invalid Information"
                            ? "alert-circle"
                            : "help-circle"
                    }
                    size={20}
                    color="#3182CE"
                    data-oid="4qkm67g"
                  />

                  <Text style={styles.modalSectionTitle} data-oid="-j34.mi">
                    {selectedIssue.issueType}
                  </Text>
                  <View
                    style={[
                      styles.modalStatusBadge,
                      {
                        backgroundColor: getStatusColor(
                          selectedIssue.status as IssueStatus,
                        ),
                      },
                    ]}
                    data-oid="u9k8674"
                  >
                    <Text style={styles.modalStatusText} data-oid="20n45qd">
                      {selectedIssue.status}
                    </Text>
                  </View>
                </View>

                <Text style={styles.modalDateText} data-oid="j1ssx95">
                  Reported on {formatDate(selectedIssue.createdAt)}
                </Text>

                <View
                  style={styles.modalDescriptionContainer}
                  data-oid="6ip0ldn"
                >
                  <Text style={styles.modalLabel} data-oid="9ntjz42">
                    Description:
                  </Text>
                  <Text style={styles.modalDescription} data-oid="hwgzs2q">
                    {selectedIssue.description}
                  </Text>
                </View>

                {selectedIssue.contactEmail && (
                  <View style={styles.modalContactContainer} data-oid="bxv_do8">
                    <Text style={styles.modalLabel} data-oid="vumq5gr">
                      Contact Email:
                    </Text>
                    <Text style={styles.modalContactEmail} data-oid="gzl9e4x">
                      {selectedIssue.contactEmail}
                    </Text>
                  </View>
                )}
              </View>

              {opportunityDetails ? (
                <View style={styles.modalSection} data-oid="clnb26i">
                  <Text style={styles.modalSectionTitle} data-oid="8ynb-zj">
                    Related Opportunity
                  </Text>

                  <View style={styles.modalOpportunityCard} data-oid="4b.xw94">
                    <Text
                      style={styles.modalOpportunityTitle}
                      data-oid="::kzn2."
                    >
                      {opportunityDetails.title}
                    </Text>
                    <Text style={styles.modalOpportunityOrg} data-oid="tmm3j9c">
                      {opportunityDetails.organization}
                    </Text>

                    <View
                      style={styles.modalOpportunityDetails}
                      data-oid="en5act1"
                    >
                      <View
                        style={styles.modalOpportunityDetail}
                        data-oid="2syzj:n"
                      >
                        <Text
                          style={styles.modalDetailLabel}
                          data-oid="blmiye5"
                        >
                          Type:
                        </Text>
                        <Text
                          style={styles.modalDetailValue}
                          data-oid="h14455b"
                        >
                          {opportunityDetails.type}
                        </Text>
                      </View>

                      <View
                        style={styles.modalOpportunityDetail}
                        data-oid="p1u6w-t"
                      >
                        <Text
                          style={styles.modalDetailLabel}
                          data-oid="y.35sxf"
                        >
                          Status:
                        </Text>
                        <Text
                          style={styles.modalDetailValue}
                          data-oid="8gn1156"
                        >
                          {opportunityDetails.status}
                        </Text>
                      </View>
                    </View>

                    <TouchableOpacity
                      style={styles.viewOpportunityButton}
                      data-oid="6wa6rd8"
                    >
                      <Text
                        style={styles.viewOpportunityText}
                        data-oid="wtjie5o"
                      >
                        View Opportunity
                      </Text>
                      <Ionicons
                        name="arrow-forward"
                        size={16}
                        color="#3182CE"
                        data-oid=".u6vi15"
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              ) : selectedIssue.opportunity ? (
                <View style={styles.modalSection} data-oid="q52qfzz">
                  <Text style={styles.modalSectionTitle} data-oid="b-hiusj">
                    Related Opportunity
                  </Text>

                  {isLoadingOpportunity ? (
                    <View style={styles.loadingContainer} data-oid="yww13t:">
                      <ActivityIndicator
                        size="small"
                        color="#3182CE"
                        data-oid="89ho9bv"
                      />

                      <Text style={styles.loadingText} data-oid="hpc4_8_">
                        Loading opportunity details...
                      </Text>
                    </View>
                  ) : (
                    <View
                      style={styles.modalOpportunityCard}
                      data-oid="e-_s4iw"
                    >
                      <Text
                        style={styles.modalOpportunityTitle}
                        data-oid="_fdyh5p"
                      >
                        {selectedIssue.opportunity.title ||
                          "Opportunity not found"}
                      </Text>
                    </View>
                  )}
                </View>
              ) : null}

              <View style={styles.modalSection} data-oid="b1qb0ys">
                <Text style={styles.modalSectionTitle} data-oid="ml8sw5s">
                  Admin Notes
                </Text>
                <TextInput
                  style={styles.modalNotesInput}
                  value={issueNotes}
                  onChangeText={setIssueNotes}
                  placeholder="Add notes about this issue..."
                  multiline
                  textAlignVertical="top"
                  numberOfLines={4}
                  placeholderTextColor="#A0AEC0"
                  data-oid="l1rkxpz"
                />
              </View>

              <View style={styles.statusActions} data-oid="kitvuw-">
                {selectedIssue.status === "Pending" && (
                  <>
                    <TouchableOpacity
                      style={[
                        styles.statusButton,
                        { backgroundColor: "#4299E1" },
                      ]}
                      onPress={() => handleUpdateStatus("Reviewed")}
                      disabled={isUpdatingStatus}
                      data-oid="-bvakc-"
                    >
                      {isUpdatingStatus ? (
                        <ActivityIndicator
                          size="small"
                          color="#FFFFFF"
                          data-oid=":aek6-y"
                        />
                      ) : (
                        <Text
                          style={styles.statusButtonText}
                          data-oid=":mcs45o"
                        >
                          Mark as Reviewed
                        </Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.statusButton,
                        { backgroundColor: "#48BB78" },
                      ]}
                      onPress={() => handleUpdateStatus("Resolved")}
                      disabled={isUpdatingStatus}
                      data-oid="o7o5e7k"
                    >
                      <Text style={styles.statusButtonText} data-oid="iing_8f">
                        Resolve Issue
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.statusButton,
                        { backgroundColor: "#F56565" },
                      ]}
                      onPress={() => handleUpdateStatus("Rejected")}
                      disabled={isUpdatingStatus}
                      data-oid="ds9vu9e"
                    >
                      <Text style={styles.statusButtonText} data-oid="iiesmls">
                        Reject
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                {selectedIssue.status === "Reviewed" && (
                  <>
                    <TouchableOpacity
                      style={[
                        styles.statusButton,
                        { backgroundColor: "#48BB78" },
                      ]}
                      onPress={() => handleUpdateStatus("Resolved")}
                      disabled={isUpdatingStatus}
                      data-oid="y:h_lbu"
                    >
                      {isUpdatingStatus ? (
                        <ActivityIndicator
                          size="small"
                          color="#FFFFFF"
                          data-oid="arbzk4p"
                        />
                      ) : (
                        <Text
                          style={styles.statusButtonText}
                          data-oid="agebsrl"
                        >
                          Resolve Issue
                        </Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.statusButton,
                        { backgroundColor: "#F56565" },
                      ]}
                      onPress={() => handleUpdateStatus("Rejected")}
                      disabled={isUpdatingStatus}
                      data-oid="uckfoud"
                    >
                      <Text style={styles.statusButtonText} data-oid="6.361h_">
                        Reject
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                {(selectedIssue.status === "Resolved" ||
                  selectedIssue.status === "Rejected") && (
                  <TouchableOpacity
                    style={[
                      styles.statusButton,
                      { backgroundColor: "#F6AD55" },
                    ]}
                    onPress={() => handleUpdateStatus("Pending")}
                    disabled={isUpdatingStatus}
                    data-oid="ayp.gs."
                  >
                    {isUpdatingStatus ? (
                      <ActivityIndicator
                        size="small"
                        color="#FFFFFF"
                        data-oid="vqbb1pf"
                      />
                    ) : (
                      <Text style={styles.statusButtonText} data-oid="kbfkacw">
                        Reopen Issue
                      </Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={styles.container} data-oid="pf5l-rb">
      <Text style={styles.title} data-oid="xe.cjda">
        Issue Reports
      </Text>

      <View style={styles.searchContainer} data-oid="57kvqb8">
        <Ionicons
          name="search"
          size={20}
          color="#718096"
          style={styles.searchIcon}
          data-oid="rqp9fbh"
        />

        <TextInput
          style={styles.searchInput}
          placeholder="Search issues..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#718096"
          data-oid="lczrtfv"
        />

        {searchQuery.length > 0 && (
          <TouchableOpacity
            onPress={() => setSearchQuery("")}
            data-oid="fbjy-co"
          >
            <Ionicons
              name="close-circle"
              size={20}
              color="#718096"
              data-oid="7mdhv_1"
            />
          </TouchableOpacity>
        )}
      </View>

      {renderStatusFilter()}

      {isLoading ? (
        <View style={styles.loadingContainer} data-oid="oc3fwxk">
          <ActivityIndicator size="large" color="#3182CE" data-oid="fhlcxql" />
          <Text style={styles.loadingText} data-oid="x:-pjul">
            Loading issues...
          </Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer} data-oid="6ju6nt-">
          <Ionicons
            name="alert-circle"
            size={36}
            color="#E53E3E"
            data-oid="01:xqcc"
          />

          <Text style={styles.errorText} data-oid="46mr::e">
            {error}
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={loadIssues}
            data-oid="ywo5.6a"
          >
            <Text style={styles.retryButtonText} data-oid="i7a0cgd">
              Retry
            </Text>
          </TouchableOpacity>
        </View>
      ) : filteredIssues.length === 0 ? (
        <View style={styles.emptyContainer} data-oid="fl8t92b">
          <Ionicons
            name="alert-circle-outline"
            size={48}
            color="#CBD5E0"
            data-oid="p05w1jn"
          />

          <Text style={styles.emptyTitle} data-oid="xil1ufd">
            No issues found
          </Text>
          <Text style={styles.emptyText} data-oid="bhb965.">
            {searchQuery.trim() !== "" || statusFilter !== "All"
              ? "Try adjusting your search criteria."
              : "There are no reported issues at this time."}
          </Text>

          {(searchQuery.trim() !== "" || statusFilter !== "All") && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSearchQuery("");
                setStatusFilter("All");
              }}
              data-oid="6wczm6s"
            >
              <Text style={styles.clearFiltersText} data-oid="dd223xa">
                Clear Filters
              </Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <FlatList
          data={filteredIssues}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          data-oid="z2_1ne1"
        />
      )}

      {renderIssueModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F7FAFC",
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 16,
    fontFamily: "Inter-SemiBold",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: "#2D3748",
    fontFamily: "Inter-Regular",
  },
  statusFilterContainer: {
    flexDirection: "row",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 8,
  },
  statusFilterButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    marginRight: 8,
  },
  statusFilterButtonActive: {
    backgroundColor: "#EBF8FF",
    borderColor: "#90CDF4",
  },
  statusFilterText: {
    fontSize: 14,
    color: "#718096",
    fontFamily: "Inter-Medium",
  },
  statusFilterTextActive: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    color: "#718096",
    fontSize: 16,
    fontFamily: "Inter-Regular",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  errorText: {
    color: "#E53E3E",
    fontSize: 16,
    textAlign: "center",
    marginTop: 16,
    marginBottom: 24,
    fontFamily: "Inter-Regular",
  },
  retryButton: {
    backgroundColor: "#3182CE",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  emptyText: {
    fontSize: 16,
    color: "#718096",
    textAlign: "center",
    marginBottom: 24,
    fontFamily: "Inter-Regular",
  },
  clearFiltersButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#E2E8F0",
    borderRadius: 8,
  },
  clearFiltersText: {
    color: "#4A5568",
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  listContent: {
    padding: 4,
  },
  issueCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginBottom: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  issueHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  dateText: {
    fontSize: 12,
    color: "#718096",
    fontFamily: "Inter-Regular",
  },
  issueContent: {
    flex: 1,
  },
  issueTypeContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  issueType: {
    fontSize: 14,
    fontWeight: "500",
    color: "#4A5568",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  issueDescription: {
    fontSize: 14,
    color: "#2D3748",
    lineHeight: 20,
    marginBottom: 12,
    fontFamily: "Inter-Regular",
  },
  opportunityReference: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#EDF2F7",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 8,
  },
  opportunityLabel: {
    fontSize: 12,
    color: "#718096",
    fontFamily: "Inter-Regular",
  },
  opportunityTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: "#4A5568",
    flex: 1,
    fontFamily: "Inter-Medium",
  },
  contactContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  contactEmail: {
    fontSize: 12,
    color: "#718096",
    marginLeft: 6,
    fontFamily: "Inter-Regular",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 10,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2D3748",
    fontFamily: "Inter-Bold",
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F7FAFC",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  modalContent: {
    padding: 16,
    maxHeight: "90%",
  },
  modalSection: {
    marginBottom: 20,
  },
  modalSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginLeft: 8,
    flex: 1,
    fontFamily: "Inter-SemiBold",
  },
  modalStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  modalStatusText: {
    fontSize: 12,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  modalDateText: {
    fontSize: 14,
    color: "#718096",
    marginBottom: 12,
    fontFamily: "Inter-Regular",
  },
  modalDescriptionContainer: {
    marginBottom: 12,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#4A5568",
    marginBottom: 4,
    fontFamily: "Inter-Medium",
  },
  modalDescription: {
    fontSize: 14,
    color: "#2D3748",
    lineHeight: 22,
    fontFamily: "Inter-Regular",
  },
  modalContactContainer: {
    marginTop: 12,
  },
  modalContactEmail: {
    fontSize: 14,
    color: "#3182CE",
    fontFamily: "Inter-Regular",
  },
  modalOpportunityCard: {
    backgroundColor: "#F7FAFC",
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  modalOpportunityTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  modalOpportunityOrg: {
    fontSize: 14,
    color: "#718096",
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  modalOpportunityDetails: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 12,
  },
  modalOpportunityDetail: {
    flexDirection: "row",
    marginRight: 16,
    marginBottom: 4,
  },
  modalDetailLabel: {
    fontSize: 14,
    color: "#718096",
    marginRight: 4,
    fontFamily: "Inter-Regular",
  },
  modalDetailValue: {
    fontSize: 14,
    fontWeight: "500",
    color: "#4A5568",
    fontFamily: "Inter-Medium",
  },
  viewOpportunityButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
  },
  viewOpportunityText: {
    fontSize: 14,
    color: "#3182CE",
    marginRight: 4,
    fontFamily: "Inter-Regular",
  },
  modalNotesInput: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 12,
    fontSize: 14,
    color: "#2D3748",
    height: 100,
    marginTop: 8,
    fontFamily: "Inter-Regular",
  },
  statusActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 8,
  },
  statusButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginLeft: 8,
  },
  statusButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
});

export default IssueReportsScreen;
